import { ref, watch } from 'vue';
import SuperTokens from 'supertokens-web-js';
import ThirdParty from 'supertokens-web-js/recipe/thirdparty';
import EmailPassword from 'supertokens-web-js/recipe/emailpassword';
import EmailVerification from 'supertokens-web-js/recipe/emailverification';
import Session from 'supertokens-web-js/recipe/session';
import MultiFactorAuth from 'supertokens-web-js/recipe/multifactorauth';
import Totp from 'supertokens-web-js/recipe/totp';
import { apiDomain, TOTP_DEVICE_NAME } from './useConstants';
import { getUserData } from '../api/user';

if (window) {
  SuperTokens.init({
    appInfo: {
      appName: 'Aristeem',
      apiDomain
    },
    recipeList: [
      EmailPassword.init(),
      EmailVerification.init(),
      ThirdParty.init({
        preAPIHook: async context => {
          const url = context.url;
          const requestInit = context.requestInit;

          if (context.action === 'THIRD_PARTY_SIGN_IN_UP') {
            const utm = context.userContext.utm;
            requestInit.body = JSON.stringify({ ...JSON.parse(requestInit.body), utm });
          }

          return { url, requestInit };
        }
      }),
      Session.init(),
      MultiFactorAuth.init(),
      Totp.init()
    ]
  });
}

const STATE = {
  LOGGED_OUT: 'LOGGED_OUT',
  LOGGED_IN: 'LOGGED_IN',
  MFA_REQUIRED: 'MFA_REQUIRED',
  EMAIL_VERIFICATION_REQUIRED: 'EMAIL_VERIFICATION_REQUIRED'
};

const user = ref(null);
const isRequested = ref(false);
const isResponsed = ref(false);

function doesSessionExist() {
  if (!window) {
    return false;
  }
  return Session.doesSessionExist();
}

async function refreshSession() {
  if (await doesSessionExist()) {
    await Session.attemptRefreshingSession();
  }
}

async function getAuthState() {
  const sessionExists = await doesSessionExist();
  if (!sessionExists) {
    return STATE.LOGGED_OUT;
  }

  const validationErrors = await Session.validateClaims();
  if (validationErrors.some(error => error.id === MultiFactorAuth.MultiFactorAuthClaim.id)) {
    return STATE.MFA_REQUIRED;
  }
  if (validationErrors.some(error => error.id === EmailVerification.EmailVerificationClaim.id)) {
    return STATE.EMAIL_VERIFICATION_REQUIRED;
  }

  return STATE.LOGGED_IN;
}

async function signIn(email, password) {
  let response;
  try {
    response = await EmailPassword.signIn({
      formFields: [
        {
          id: 'email',
          value: email
        },
        {
          id: 'password',
          value: password
        }
      ]
    });
  } catch (error) {
    return { success: false, error: error.message };
  }

  if (response?.status === 'OK') {
    isRequested.value = false;
    await getUserAsync();
    return { success: true };
  }

  // known statuses are: OK, WRONG_CREDENTIALS_ERROR, FIELD_ERROR
  return { success: false, error: response?.status };
}

async function signOut() {
  user.value = null;
  await Session.signOut();
}

// NOTE: possibly we should check email for all strategies (email, google, facebook)
async function doesEmailExist(email) {
  let response;
  try {
    response = await EmailPassword.doesEmailExist(email);
  } catch (error) {
    return { success: false, error: error.message };
  }

  if (response.status === 'OK') {
    return { success: true, doesExist: response.doesExist };
  }

  return { success: false, error: 'UNKNOWN_RESPONSE' };
}

async function getUserAsync() {
  if (isRequested.value) {
    if (isResponsed.value) {
      return user;
    }

    return new Promise(resolve => {
      const unwatch = watch(isResponsed, value => {
        if (value) {
          resolve(user);
          unwatch();
        }
      });
    });
  }

  isRequested.value = true;

  const sessionExists = await doesSessionExist();
  if (!sessionExists) {
    isRequested.value = false;
    return user;
  }

  try {
    const { data, statusCode } = await getUserData({ onError: res => ({ data: null, statusCode: res?.status || 500 }) });

    if (statusCode === 403) {
      isRequested.value = true;
      isResponsed.value = true;
      user.value = null;
      return user;
    }

    if (statusCode !== 200) {
      isRequested.value = false;
      return user;
    }

    setUser(data);
    isResponsed.value = true;

  } catch (error) {
    isRequested.value = true;
    console.error('Error getting User', error);
  }

  return user;
}

function getUser(force = false) {
  if (force) {
    isRequested.value = false;
    isResponsed.value = false;
  }

  if (!isRequested.value) {
    getUserAsync();
  }

  return user;
}

function setUser(newUser) {
  user.value = { ...newUser };
}

async function signUp(email, password, isInMailList = false, utm = null) {
  let response;
  try {
    response = await EmailPassword.signUp({
      formFields: [
        {
          id: 'email',
          value: email
        },
        {
          id: 'password',
          value: password
        },
        {
          id: 'isInMailList',
          value: isInMailList
        },
        {
          id: 'utm',
          value: utm
        }
      ]
    });

  } catch (error) {
    return { success: false, error: error.message };
  }

  // FIXME: different status codes
  if (response?.status === "FIELD_ERROR") {
    // Custom error for EMAIL_ALREADY_EXISTS_ERROR
    if (response.formFields[0] && response.formFields[0].error === 'This email already exists. Please sign in instead.') {
      return { success: false, error: 'This email already exists. Please log in.' };
    }

    return { success: false, error: response.formFields[0].error };
  }

  return { success: true };
}

async function sendVerificationEmail() {
  try {
    const response = await EmailVerification.sendVerificationEmail();
    if (response.status === "EMAIL_ALREADY_VERIFIED_ERROR") {
      return { success: false, isAlreadyVerified: true };
    }

    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

function thirdpartySignInAndUp(utm = null) {
  const input = utm ? { userContext: { utm } } : undefined;
  return ThirdParty.signInAndUp(input);
}

async function createNewTotpDevice() {
  if (!(await Session.doesSessionExist())) {
    return { error: "User must be logged in" };
  }

  let response;
  try {
    response = await Totp.createDevice({ deviceName: TOTP_DEVICE_NAME });
  } catch (err) {
    console.log('TOTP Error', err);
  }

  if (response?.status === "OK") {
    return { qrCodeString: response.qrCodeString, secret: response.secret, deviceName: response.deviceName };
  }

  return { error: response?.reason || 'Unable to create TOTP device' };
}

async function verifyTotpDevice(totp) {
  if (!(await Session.doesSessionExist())) {
    return { error: "User must be logged in" };
  }

  let response;
  try {
    response = await Totp.verifyDevice({
      deviceName: TOTP_DEVICE_NAME,
      totp
    });
  } catch (err) {
    console.log('TOTP Error', err);
  }

  if (response?.status === "OK") {
    return { success: true };
  }

  return { error: response?.reason || 'Unable to verify TOTP device' };
}

async function verifyTotpCode(totp) {
  if (!(await Session.doesSessionExist())) {
    return { error: "User must be logged in" };
  }

  let response;
  try {
    response = await Totp.verifyCode({
      totp
    });
  } catch (err) {
    console.log('TOTP Error', err);
  }

  if (response.status === "OK") {
    return { success: true };
  }

  return { error: response?.reason || 'Unable to verify TOTP code' };
}

async function isAllMFACompleted() {
  if (await Session.doesSessionExist()) {
    const mfaClaim = await Session.getClaimValue({
      claim: MultiFactorAuth.MultiFactorAuthClaim
    });

    if (mfaClaim === undefined) {
      // this can happen during migration where the session is an older one
      // that was created before MFA was introduced on the backend
      return true;
    }

    return mfaClaim.v;
  }

  throw new Error("Illegal function call: For first factor setup, you do not need to call this function");
}

async function mfaInfo() {
  const info = await MultiFactorAuth.resyncSessionAndFetchMFAInfo(); // This is not working
  // const info = await fetch(`${apiDomain}/auth/mfa/info`, {
  //   method: 'PUT',
  //   headers: {
  //     'Content-Type': 'application/json'
  //   },
  //   body: JSON.stringify({})
  // });
  console.log('MFA Info', info);
}

async function sendForgotPasswordEmail(email) {
  let response;
  try {
    response = await EmailPassword.sendPasswordResetEmail({
      formFields: [
        {
          id: 'email',
          value: email
        }
      ]
    });
  } catch (error) {
    return { success: false, error: error.message };
  }

  if (response?.status === 'OK') {
    return { success: true };
  }

  return { success: false, error: response?.status };
}

async function submitNewPassword(password) {
  let response;
  try {
    response = await EmailPassword.submitNewPassword({
      formFields: [
        {
          id: 'password',
          value: password
        }
      ]
    });
  } catch (error) {
    return { success: false, error: error.message };
  }

  if (response?.status === 'OK') {
    return { success: true };
  }

  return { success: false, error: response?.status };
}

export {
  STATE,
  getUser,
  getUserAsync,
  getAuthState,
  doesSessionExist,
  doesEmailExist,
  signIn,
  signOut,
  signUp,
  createNewTotpDevice,
  verifyTotpDevice,
  verifyTotpCode,
  thirdpartySignInAndUp,
  isAllMFACompleted,
  mfaInfo,
  sendForgotPasswordEmail,
  submitNewPassword,
  sendVerificationEmail,
  refreshSession
};
